import { useEffect } from 'react';
import { useUnmount } from 'react-use';

import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import {
   VisitSettlementSimple,
   VisitSettlementSimpleQuery,
   VisitSettlementStatus,
} from '@dreryk/egabinet-financial-settlement-frontend-api';
import { PurchaseId } from '@dreryk/egabinet-financial-settlement-published-language-api';

import { useCqrsSearch } from '@dreryk/command-query-react-common';
import { paginations } from '@dreryk/command-query-ws-client';
import { dispatchSnackbarNegativeEvent } from '@dreryk/react-components';

import { ObjectMap } from '~libs.typescript';

import { translationKeys } from '../../translations';

import { useVisitSettlementStatusGlobalState } from './useVisitSettlementStatusGlobalState';

const DEFAULT_PAGINATION = paginations.all();

type VisitSettlementStatusResolverProps = {
   readonly appointmentIds: number[];
};

export const VisitSettlementStatusResolver = (props: VisitSettlementStatusResolverProps): null => {
   const { updateStatus, resetStatus } = useVisitSettlementStatusGlobalState();

   const { data: visitSettlements } = useCqrsSearch<VisitSettlementSimple>(
      () => {
         const query = new VisitSettlementSimpleQuery();
         query.purchaseIds = props.appointmentIds.map((appointmentId) =>
            PurchaseId.forAppointment(AppointmentId.of(appointmentId))
         );
         return query;
      },
      DEFAULT_PAGINATION,
      {
         onUnexpectedError: () => {
            dispatchSnackbarNegativeEvent(translationKeys.serviceIsCurrentlyUnavailable);
         },
      }
   );

   useEffect(() => {
      if (!visitSettlements) {
         return;
      }
      const settlementStatusMap = new ObjectMap<PurchaseId, VisitSettlementStatus>(
         visitSettlements.map((status) => [status.purchaseId, status.settlementStatus])
      );
      const visitSettlementStatusMap: Map<number, VisitSettlementStatus> = new Map(
         props.appointmentIds.map((appointmentId) => [
            appointmentId,
            settlementStatusMap.get(PurchaseId.forAppointment(AppointmentId.of(appointmentId))) ?? 'UNSETTLED',
         ])
      );

      updateStatus(visitSettlementStatusMap);
   }, [visitSettlements, props.appointmentIds, updateStatus]);

   useUnmount(resetStatus);

   return null;
};
